import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { TenantRole } from '@detect/shared';
import { useAuth } from './contexts/AuthContext';
import { useTenant } from './contexts/TenantContext';
import { AppInitializing } from './components/common/AppInitializing';
import { NoTenant } from './pages/NoTenant';

/**
 * Tenant-entry route guards (frontend-patterns §8.3), split out of
 * router.tsx so they can be unit-tested without importing every page
 * (#39). Behavior is unchanged from when they lived in the router.
 */

export function RequireTenant({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  // A superadmin who entered a tenant from Platform admin has an active
  // tenantId without any membership. Checking memberships alone sent
  // "Open" straight back to NoTenant's create-organization screen. The
  // exception is explicit to superadmins; TenantContext also re-validates
  // tenantId against the current user on every render.
  const enteredAsSuperadmin = user?.globalRole === 'superadmin' && tenantId !== null;
  if (user && user.tenants.length === 0 && !enteredAsSuperadmin) return <NoTenant />;
  if (!tenantId) return <AppInitializing message="Selecting tenant…" />;
  return <>{children}</>;
}

export function RequireRole({
  role,
  children,
}: {
  role: TenantRole;
  children: ReactNode;
}) {
  const { hasRole } = useTenant();
  if (!hasRole(role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}
